/**
 * Cross-deck usage hooks for tracking card usage across all decks
 *
 * These hooks enable features like:
 * - "In X decks" indicators on cards
 * - Cross-deck usage section in card details panel
 * - Total needed calculations for deck needs feature
 */
import { useMemo } from 'react';

import { useAtomValue } from 'jotai';

import { useCardLookup } from '@/hooks/useCardLookup';
import type { DeckStatus } from '@/types/deck';
import { allDeckCardsAtom } from '../state/allDeckCards';
import { decksStateAtom } from '../state/decksAtom';

// ============================================================================
// Types
// ============================================================================

export interface DeckUsage {
  deckId: string;
  deckName: string;
  deckStatus: DeckStatus;
  quantity: number;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Get all decks that use a specific card (by name).
 * Excludes archived decks by default.
 *
 * @param cardNameLower - Lowercase card name to search for (null to skip)
 * @param excludeDeckId - Optional deck ID to exclude from results (e.g., current deck)
 * @returns Array of deck usages sorted by status (active first) then quantity
 *
 * @example
 * ```tsx
 * const usages = useCrossDeckUsage('lightning bolt', currentDeckId);
 * // => [{ deckId: 'abc', deckName: 'Burn', deckStatus: 'active', quantity: 4 }, ...]
 * ```
 */
export function useCrossDeckUsage(cardNameLower: string | null, excludeDeckId?: string): DeckUsage[] {
  const allDeckCards = useAtomValue(allDeckCardsAtom);
  const decks = useAtomValue(decksStateAtom);
  const { getCard } = useCardLookup();

  return useMemo(() => {
    if (!cardNameLower || !allDeckCards.data || !decks.data) return [];

    // Build deck lookup
    const deckById = new Map(decks.data.map((d) => [d.id, d]));

    // Aggregate cards by deck
    const usageByDeck = new Map<string, number>();

    for (const deckCard of allDeckCards.data) {
      if (deckCard.deck_id === excludeDeckId) continue;
      if (deckCard.zone === 'maybeboard') continue;

      // Use useCardLookup for unified fallback
      const card = getCard(deckCard.scryfall_id);
      if (card && card.name_lower === cardNameLower) {
        const current = usageByDeck.get(deckCard.deck_id) ?? 0;
        usageByDeck.set(deckCard.deck_id, current + deckCard.quantity);
      }
    }

    // Convert to array with deck info
    const usages: DeckUsage[] = [];
    for (const [deckId, quantity] of usageByDeck) {
      const deck = deckById.get(deckId);
      if (!deck || deck.status === 'archived') continue;

      usages.push({
        deckId,
        deckName: deck.name,
        deckStatus: deck.status,
        quantity,
      });
    }

    // Sort: active first, then by quantity descending
    return usages.sort((a, b) => {
      if (a.deckStatus === 'active' && b.deckStatus !== 'active') return -1;
      if (b.deckStatus === 'active' && a.deckStatus !== 'active') return 1;
      return b.quantity - a.quantity;
    });
  }, [cardNameLower, excludeDeckId, allDeckCards.data, decks.data, getCard]);
}

/**
 * Get total quantity needed of a specific card across all non-archived decks.
 * Useful for comparing against collection to see shortfall.
 *
 * @param cardNameLower - Lowercase card name to search for (null to skip)
 * @param excludeDeckId - Optional deck ID to exclude from total
 * @returns Total quantity needed across all decks
 *
 * @example
 * ```tsx
 * const totalNeeded = useTotalNeededAcrossDecks('lightning bolt');
 * // If 3 decks each have 4 copies => 12
 * ```
 */
export function useTotalNeededAcrossDecks(cardNameLower: string | null, excludeDeckId?: string): number {
  const usages = useCrossDeckUsage(cardNameLower, excludeDeckId);

  return useMemo(() => {
    return usages.reduce((total, usage) => total + usage.quantity, 0);
  }, [usages]);
}

/**
 * Get the count of decks using a specific card.
 * Useful for "In X decks" indicators.
 *
 * @param cardNameLower - Lowercase card name to search for (null to skip)
 * @param excludeDeckId - Optional deck ID to exclude from count
 * @returns Number of decks using this card
 */
export function useDeckCountForCard(cardNameLower: string | null, excludeDeckId?: string): number {
  const usages = useCrossDeckUsage(cardNameLower, excludeDeckId);
  return usages.length;
}

/**
 * Get the quantity of a specific card in a specific deck.
 * Used by AddToDeckMenu to show copy counts for each deck.
 *
 * @param cardNameLower - Lowercase card name to search for (null to skip)
 * @param deckId - The deck ID to check (null to skip)
 * @returns Quantity of the card in the specified deck
 */
export function useCardCountInDeck(cardNameLower: string | null, deckId: string | null | undefined): number {
  const allDeckCards = useAtomValue(allDeckCardsAtom);
  const { getCard } = useCardLookup();

  return useMemo(() => {
    if (!cardNameLower || !deckId || !allDeckCards.data) return 0;

    let count = 0;
    for (const deckCard of allDeckCards.data) {
      if (deckCard.deck_id !== deckId) continue;

      // Use useCardLookup for unified fallback
      const card = getCard(deckCard.scryfall_id);
      if (card && card.name_lower === cardNameLower) {
        count += deckCard.quantity;
      }
    }

    return count;
  }, [cardNameLower, deckId, allDeckCards.data, getCard]);
}
